import React, { useEffect, useMemo, useState } from 'react'
import ReminderItem from './ReminderItem'
import { computeReminderList } from './useAdminFee'

function fmtMoney(n) {
    const v = Number(n) || 0
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Decimal-hours <-> HH:MM helpers.
function decimalToHHMM(decimal) {
    const v = Number(decimal) || 0
    const h = Math.floor(v)
    const m = Math.round((v - h) * 60)
    return `${h}:${String(m).padStart(2, '0')}`
}
function hhmmToDecimal(s) {
    if (!s) return 0
    if (typeof s === 'number') return s
    const [h, m] = String(s).split(':')
    return (Number(h) || 0) + ((Number(m) || 0) / 60)
}

// HoursInput — Scale-parity TimeInputSplit behavior. Holds a local string
// while typing so the user can freely type "1:30" without each keystroke
// re-converting through decimal hours (which mangles partial input).
// On blur we normalize to HH:MM, clamp minutes to 59, then commit the
// resulting decimal hours to the parent.
export function HoursInput({ valueDecimal, onChangeDecimal }) {
    const [text, setText] = React.useState(() => decimalToHHMM(valueDecimal))
    // Re-sync if parent value changes from elsewhere (e.g. rate update,
    // draft load) — but only when not actively focused, to avoid wiping
    // in-flight typing.
    const inputRef = React.useRef(null)
    React.useEffect(() => {
        if (document.activeElement !== inputRef.current) {
            setText(decimalToHHMM(valueDecimal))
        }
    }, [valueDecimal])

    const handleChange = (e) => {
        const raw = e.target.value
        // Allow only digits and colon. Auto-insert colon if the user has
        // typed >3 digits with no colon yet (matches Scale's TimeInputSplit).
        let sanitized = raw.replace(/[^0-9:]/g, '')
        if (raw.endsWith(':') && sanitized === '') sanitized = ':'
        if (sanitized.length > 3 && !sanitized.includes(':')) {
            sanitized = `${sanitized.slice(0, 3)}:${sanitized.slice(3)}`
        }
        setText(sanitized)
    }
    const handleBlur = () => {
        let [hours = '', minutes = ''] = text.split(':')
        hours = hours || '0'
        minutes = minutes || '0'
        let mNum = Math.min(parseInt(minutes, 10) || 0, 59)
        // Edge case from Scale: 4 digits with no colon → first 3 are hours,
        // remainder forced to 59.
        if (text.length > 3 && !text.includes(':')) {
            hours = text.slice(0, 3)
            mNum = 59
        }
        const hoursStr = String(parseInt(hours, 10) || 0)
        const minStr = String(mNum).padStart(2, '0')
        const formatted = `${hoursStr}:${minStr}`
        setText(formatted)
        onChangeDecimal(hhmmToDecimal(formatted))
    }
    return (
        <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="1:00"
            inputMode="numeric"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-center"
        />
    )
}

// Step 2 of 3 — "Invoice Details". Due date + line items + reminders +
// (optional) per-side allocation + notes. Mirrors Scale's InvoiceSecondStep.
export default function InvoiceSecondStep({
    values,
    setFieldValue,
    effectiveHourlyRate,
    mediatorReminderSettings,
    showAllocationToggle = false,
}) {
    const items = values.items || []
    const dueDate = values.due_date || ''
    const reminders = values.reminders || {}
    const [openReminders, setOpenReminders] = useState(false)
    const [editingReminder, setEditingReminder] = useState(null)

    // Auto-add a starter line item the first time we land on step 2.
    useEffect(() => {
        if ((!items || items.length === 0)) {
            setFieldValue('items', [{
                id: 1,
                description: '',
                quantity: 1,
                rate: Number(effectiveHourlyRate) || 0,
                amount: Number(effectiveHourlyRate) || 0,
            }])
        }
    // eslint-disable-next-line
    }, [])

    // Scale parity (InvoiceSecondStep:116-124): sync existing items' rate when
    // effectiveHourlyRate changes (e.g. pricing tier kicks in for >2 parties).
    useEffect(() => {
        const rate = Number(effectiveHourlyRate)
        if (!rate || !items || items.length === 0) return
        const needsUpdate = items.some((it) => Number(it.rate) !== rate)
        if (!needsUpdate) return
        setFieldValue('items', items.map((it) => ({
            ...it,
            rate,
            amount: (Number(it.quantity) || 0) * rate,
        })))
    // eslint-disable-next-line
    }, [effectiveHourlyRate])

    // Scale parity (InvoiceSecondStep:86-99): auto-set due_date on first land
    // when mediator has hourly_invoice_reminder_settings.due_date_days.
    useEffect(() => {
        if (dueDate) return
        const days = Number(mediatorReminderSettings?.due_date_days)
        if (!days || Number.isNaN(days)) return
        const d = new Date()
        d.setDate(d.getDate() + days)
        setFieldValue('due_date', d.toISOString().slice(0, 10))
    // eslint-disable-next-line
    }, [mediatorReminderSettings])

    // When due_date changes, auto-recalc reminders unless user has manually
    // edited them (we always recalc — Scale parity).
    useEffect(() => {
        if (!dueDate) return
        const list = computeReminderList(dueDate, mediatorReminderSettings)
        if (list) setFieldValue('reminders', list)
    // eslint-disable-next-line
    }, [dueDate])

    const subtotal = useMemo(
        () => (items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0),
        [items]
    )

    const updateItem = (idx, patch) => {
        const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it))
        // Re-derive amount if quantity or rate changed
        const last = next[idx]
        next[idx] = { ...last, amount: (Number(last.quantity) || 0) * (Number(last.rate) || 0) }
        setFieldValue('items', next)
    }

    const addItem = () => {
        const maxId = items.reduce((m, it) => Math.max(m, Number(it.id) || 0), 0)
        setFieldValue('items', [
            ...items,
            { id: maxId + 1, description: '', quantity: 1, rate: Number(effectiveHourlyRate) || 0, amount: Number(effectiveHourlyRate) || 0 },
        ])
    }
    const removeItem = (idx) => {
        const next = items.filter((_, i) => i !== idx)
        setFieldValue('items', next)
    }

    return (
        <div className="space-y-5">
            <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Invoice Details</p>
                <h3 className="text-base font-semibold text-teal-800">Invoice Details</h3>
                <p className="text-sm text-gray-500 mt-0.5">Add invoice details and line items.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                    <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => setFieldValue('due_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        min={new Date().toISOString().split('T')[0]}
                    />
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Line Items</label>
                </div>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2 text-left">Description</th>
                                <th className="px-3 py-2 text-left w-28">Hours:Minutes</th>
                                <th className="px-3 py-2 text-left w-28">Rate ($/hr)</th>
                                <th className="px-3 py-2 text-right w-32">Amount</th>
                                <th className="px-3 py-2 w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it, i) => (
                                <tr key={it.id || i} className="border-t border-gray-200">
                                    <td className="px-3 py-2">
                                        <input
                                            type="text"
                                            value={it.description}
                                            onChange={(e) => updateItem(i, { description: e.target.value })}
                                            placeholder="eg. Mediation Prep"
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded"
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <HoursInput
                                            valueDecimal={it.quantity}
                                            onChangeDecimal={(v) => updateItem(i, { quantity: v })}
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="relative">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={it.rate}
                                                onChange={(e) => updateItem(i, { rate: Number(e.target.value) })}
                                                className="w-full pl-5 pr-2 py-1.5 border border-gray-300 rounded"
                                            />
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700">${fmtMoney(it.amount)}</td>
                                    <td className="px-3 py-2 text-right">
                                        {items.length > 1 ? (
                                            <button type="button" onClick={() => removeItem(i)} className="text-gray-400 hover:text-red-500">×</button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-50">
                            <tr>
                                <td colSpan={3} className="px-3 py-2 text-right text-xs uppercase text-gray-500">Subtotal</td>
                                <td className="px-3 py-2 text-right font-semibold" style={{ color: '#027776' }}>${fmtMoney(subtotal)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <button type="button" onClick={addItem} className="mt-2 text-sm font-medium" style={{ color: '#115E59' }}>+ Add Item</button>
            </div>

            {/* Payment Reminders accordion */}
            <div className="border border-gray-200 rounded-lg">
                <button
                    type="button"
                    onClick={() => setOpenReminders((p) => !p)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50"
                >
                    <span className="text-sm font-semibold text-gray-700">Payment Reminders</span>
                    <span className="text-gray-400 text-xs">
                        {openReminders ? '▲' : '▼'}
                    </span>
                </button>
                {!openReminders && reminders.firstReminder ? (
                    <div className="px-4 pb-3 text-xs text-gray-600">
                        First Reminder will be send on <span className="font-semibold">{new Date(reminders.firstReminder).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                ) : null}
                {openReminders && (
                    <div className="px-4 pb-3 divide-y divide-gray-100">
                        {[
                            { key: 'firstReminder', label: 'Invoice Due Email' },
                            { key: 'secondReminder', label: 'Reminder 1' },
                            { key: 'thirdReminder', label: 'Reminder 2' },
                            { key: 'fourthReminder', label: 'Reminder 3' },
                        ].map((row) => (
                            <ReminderItem
                                key={row.key}
                                label={row.label}
                                value={reminders[row.key]}
                                isEditing={editingReminder === row.key}
                                onStartEdit={() => setEditingReminder(row.key)}
                                onCancel={() => setEditingReminder(null)}
                                onUpdate={(d) => {
                                    setFieldValue('reminders', { ...reminders, [row.key]: d })
                                    setEditingReminder(null)
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>

            {showAllocationToggle && values.selectedOption !== 'one' && (
                <div className="border border-gray-200 rounded-lg p-4">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Invoice Allocation by Party</div>
                    <div className="space-y-2">
                        {[
                            { v: 'equally_split', label: 'Equally Split Between All Parties' },
                            { v: '50_percent', label: '50% Split by Side (Plaintiff / Defendant)' },
                        ].map((opt) => (
                            <label key={opt.v} className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="invoice_allocation_party"
                                    checked={values.invoice_allocation_party === opt.v}
                                    onChange={() => setFieldValue('invoice_allocation_party', opt.v)}
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                    value={values.additional_notes || ''}
                    onChange={(e) => setFieldValue('additional_notes', e.target.value)}
                    rows={3}
                    placeholder="Additional Notes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
            </div>
        </div>
    )
}
